import { useState, useCallback, useEffect, useRef } from 'react';
import { TimeRangePreset } from '../../Settings/reportDsl';
import { fmtDate, toLocalDatetimeStr } from '../../Logs/utils';
import {
  REPORTS_QUICK_RANGES,
  REPORTS_DEFAULT_QUICK_RANGE,
  ReportsQuickRange,
  resolveReportsPresetBounds,
} from '../reportsTimeRange';

/**
 * localStorage key for the shared Reports time-range selection.
 *
 * The selection is stored **per server + report suite** (the `scopeKey`
 * passed by the caller). Both the Reports dashboard / level-1 drill-down
 * (Reports.tsx) and the per-user level-2 page (UserDrillDownPage.tsx) read &
 * write this map, so the chosen window survives navigation, drill-downs,
 * sidebar re-clicks and reloads — independently for each server/suite.
 *
 * When a server/suite has no stored selection, the caller-supplied
 * `defaultTimeRangeLabel` (configured on the ClickHouse server in Settings)
 * is applied; if that is empty/invalid the built-in default is used.
 */
const TIME_RANGE_STORAGE_KEY = 'infralytiqs-reports-timerange-v2';

interface StoredTimeRange {
  timeRangeLabel: string;
  activePreset?: TimeRangePreset;
  activeRangeMs: number;
  absoluteFrom: number;
  absoluteTo: number;
  isRelative: boolean;
}

type StoredTimeRangeMap = Record<string, StoredTimeRange>;

const DAY_MS = 24 * 60 * 60 * 1000;

const isValidStoredEntry = (p: Partial<StoredTimeRange> | null | undefined): p is StoredTimeRange => {
  if (!p || typeof p.timeRangeLabel !== 'string' || typeof p.isRelative !== 'boolean') return false;
  if (typeof p.activeRangeMs !== 'number' || typeof p.absoluteFrom !== 'number' || typeof p.absoluteTo !== 'number') {
    return false;
  }
  return true;
};

const loadStoredMap = (): StoredTimeRangeMap => {
  try {
    const raw = localStorage.getItem(TIME_RANGE_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as StoredTimeRangeMap;
    if (!parsed || typeof parsed !== 'object') return {};
    return parsed;
  } catch {
    return {};
  }
};

const loadScopeEntry = (scopeKey: string): StoredTimeRange | null => {
  if (!scopeKey) return null;
  const entry = loadStoredMap()[scopeKey];
  return isValidStoredEntry(entry) ? entry : null;
};

const saveScopeEntry = (scopeKey: string, entry: StoredTimeRange): void => {
  if (!scopeKey) return;
  try {
    const map = loadStoredMap();
    map[scopeKey] = entry;
    localStorage.setItem(TIME_RANGE_STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* ignore quota / serialization errors — persistence is best-effort */
  }
};

/** Build a persisted snapshot from a quick range (preset-aware). */
const quickRangeToStored = (range: ReportsQuickRange): StoredTimeRange => {
  const now = Date.now();
  if (range.preset) {
    const bounds = resolveReportsPresetBounds(range.preset);
    return {
      timeRangeLabel: range.label,
      activePreset: range.preset,
      activeRangeMs: range.ms ?? (bounds.end - bounds.start),
      absoluteFrom: bounds.start,
      absoluteTo: bounds.end,
      isRelative: true,
    };
  }
  const activeRangeMs = range.ms ?? DAY_MS;
  return {
    timeRangeLabel: range.label,
    activePreset: undefined,
    activeRangeMs,
    absoluteFrom: now - activeRangeMs,
    absoluteTo: now,
    isRelative: true,
  };
};

interface UseReportsTimeRangeOptions {
  /**
   * Identifies the server + suite the selection belongs to (e.g.
   * `${configId}::${suiteId}`). When it changes, the hook restores that
   * scope's stored selection or applies the default. Empty until the active
   * server/suite is known — the hook holds the built-in default meanwhile.
   */
  scopeKey?: string;
  /**
   * Quick-range label configured as the server's default time range. Applied
   * when the active `scopeKey` has no stored history in the browser.
   */
  defaultTimeRangeLabel?: string;
}

export function useReportsTimeRange(options: UseReportsTimeRangeOptions = {}) {
  const { scopeKey = '', defaultTimeRangeLabel = '' } = options;

  const [timeRangeLabel, setTimeRangeLabel] = useState(REPORTS_DEFAULT_QUICK_RANGE.label);
  const [activePreset, setActivePreset] = useState<TimeRangePreset | undefined>(
    REPORTS_DEFAULT_QUICK_RANGE.preset,
  );
  const [activeRangeMs, setActiveRangeMs] = useState(REPORTS_DEFAULT_QUICK_RANGE.ms ?? DAY_MS);
  const [absoluteFrom, setAbsoluteFrom] = useState<number>(
    Date.now() - (REPORTS_DEFAULT_QUICK_RANGE.ms ?? DAY_MS),
  );
  const [absoluteTo, setAbsoluteTo] = useState<number>(Date.now());
  const [isRelative, setIsRelative] = useState(true);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  // Tracks the scope whose selection is currently loaded, and the scope the
  // resolution effect has already handled (so default/stored is applied once
  // per scope and never clobbers a user's later change).
  const scopeRef = useRef<string>('');
  const resolvedScopeRef = useRef<string | null>(null);

  /** Apply a snapshot to state, optionally persisting it for the active scope. */
  const commit = useCallback((snapshot: StoredTimeRange, persist: boolean) => {
    setTimeRangeLabel(snapshot.timeRangeLabel);
    setActivePreset(snapshot.activePreset);
    setActiveRangeMs(snapshot.activeRangeMs);
    setAbsoluteFrom(snapshot.absoluteFrom);
    setAbsoluteTo(snapshot.absoluteTo);
    setIsRelative(snapshot.isRelative);
    if (persist && scopeRef.current) {
      saveScopeEntry(scopeRef.current, snapshot);
    }
  }, []);

  // Restore the persisted selection for the active server/suite, or apply the
  // server's configured default when there is no stored history for it. Runs
  // once per distinct scopeKey so it never overrides a user's later change.
  useEffect(() => {
    if (!scopeKey || resolvedScopeRef.current === scopeKey) return;
    resolvedScopeRef.current = scopeKey;
    scopeRef.current = scopeKey;

    const stored = loadScopeEntry(scopeKey);
    if (stored) {
      commit(stored, false);
      return;
    }
    const defaultRange = defaultTimeRangeLabel
      ? REPORTS_QUICK_RANGES.find((r) => r.label === defaultTimeRangeLabel)
      : undefined;
    commit(quickRangeToStored(defaultRange ?? REPORTS_DEFAULT_QUICK_RANGE), false);
  }, [scopeKey, defaultTimeRangeLabel, commit]);

  const getTimeRangeMs = useCallback((): { start: number; end: number } => {
    if (!isRelative) {
      return { start: absoluteFrom, end: absoluteTo };
    }
    if (activePreset) {
      return resolveReportsPresetBounds(activePreset);
    }
    const now = Date.now();
    return { start: now - activeRangeMs, end: now };
  }, [isRelative, activePreset, activeRangeMs, absoluteFrom, absoluteTo]);

  const handleTimePickerOpen = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  }, []);

  const handleQuickRangeSelect = useCallback((range: ReportsQuickRange) => {
    commit(quickRangeToStored(range), true);
    setAnchorEl(null);
  }, [commit]);

  const handleApplyCustomRange = useCallback((fromMs: number, toMs: number, label: string) => {
    commit({
      timeRangeLabel: label,
      activePreset: undefined,
      activeRangeMs: toMs - fromMs,
      absoluteFrom: fromMs,
      absoluteTo: toMs,
      isRelative: false,
    }, true);
    setAnchorEl(null);
  }, [commit]);

  const shiftTime = useCallback((direction: 'back' | 'forward') => {
    const shiftMs = direction === 'back' ? -activeRangeMs : activeRangeMs;
    const now = Date.now();

    let currentFrom: number;
    let currentTo: number;
    if (isRelative && activePreset) {
      const bounds = resolveReportsPresetBounds(activePreset);
      currentFrom = bounds.start;
      currentTo = bounds.end;
    } else if (isRelative) {
      currentTo = now;
      currentFrom = now - activeRangeMs;
    } else {
      currentFrom = absoluteFrom;
      currentTo = absoluteTo;
    }

    let newFrom = currentFrom + shiftMs;
    let newTo = currentTo + shiftMs;

    if (direction === 'forward' && newTo > now) {
      newTo = now;
      newFrom = now - activeRangeMs;
      const quickRange = REPORTS_QUICK_RANGES.find((r) => r.label === timeRangeLabel);
      if (quickRange) {
        handleQuickRangeSelect(quickRange);
      } else {
        commit({
          timeRangeLabel: `${fmtDate(new Date(newFrom))} to ${fmtDate(new Date(newTo))}`,
          activePreset: undefined,
          activeRangeMs,
          absoluteFrom: newFrom,
          absoluteTo: newTo,
          isRelative: false,
        }, true);
      }
      return;
    }

    commit({
      timeRangeLabel: `${fmtDate(new Date(newFrom))} to ${fmtDate(new Date(newTo))}`,
      activePreset: undefined,
      activeRangeMs,
      absoluteFrom: newFrom,
      absoluteTo: newTo,
      isRelative: false,
    }, true);
  }, [activeRangeMs, isRelative, activePreset, absoluteFrom, absoluteTo, timeRangeLabel, handleQuickRangeSelect, commit]);

  const handleZoomOut = useCallback(() => {
    const quickRange = REPORTS_QUICK_RANGES.find((r) => r.label === timeRangeLabel);
    if (quickRange) {
      const idx = REPORTS_QUICK_RANGES.indexOf(quickRange);
      if (idx < REPORTS_QUICK_RANGES.length - 1) {
        handleQuickRangeSelect(REPORTS_QUICK_RANGES[idx + 1]);
      }
    }
  }, [timeRangeLabel, handleQuickRangeSelect]);

  const handleZoomIn = useCallback(() => {
    const quickRange = REPORTS_QUICK_RANGES.find((r) => r.label === timeRangeLabel);
    if (quickRange) {
      const idx = REPORTS_QUICK_RANGES.indexOf(quickRange);
      if (idx > 0) {
        handleQuickRangeSelect(REPORTS_QUICK_RANGES[idx - 1]);
      }
    } else {
      const closestIdx = REPORTS_QUICK_RANGES.findIndex((r) => (r.ms ?? 0) >= activeRangeMs);
      const targetIdx = closestIdx > 0 ? closestIdx - 1 : 0;
      handleQuickRangeSelect(REPORTS_QUICK_RANGES[targetIdx]);
    }
  }, [timeRangeLabel, activeRangeMs, handleQuickRangeSelect]);

  const getPickerInitialDates = useCallback(() => {
    const { start, end } = getTimeRangeMs();
    return {
      from: toLocalDatetimeStr(new Date(start)),
      to: toLocalDatetimeStr(new Date(end)),
    };
  }, [getTimeRangeMs]);

  const tzOffset = new Date().getTimezoneOffset();
  const tzSign = tzOffset <= 0 ? '+' : '-';
  const tzHours = String(Math.floor(Math.abs(tzOffset) / 60)).padStart(2, '0');
  const tzMins = String(Math.abs(tzOffset) % 60).padStart(2, '0');
  const tzName = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const tzString = `UTC${tzSign}${tzHours}:${tzMins}`;

  return {
    timeRangeLabel,
    activePreset,
    anchorEl,
    setAnchorEl,
    getTimeRangeMs,
    handleTimePickerOpen,
    handleQuickRangeSelect,
    handleApplyCustomRange,
    shiftTime,
    handleZoomOut,
    handleZoomIn,
    getPickerInitialDates,
    tzName,
    tzString,
  };
}
